import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/* What the banner renders. `status` falls back to 'error' there. */
export interface MilitaryVerificationBannerError {
  headline: string;
  message: ReactNode;
  status?: string;
}

interface MilitaryVerificationErrorContextValue {
  error: MilitaryVerificationBannerError | null;
  setError: (error: MilitaryVerificationBannerError | null) => void;
  clearError: () => void;
}

const noop = () => {};

export const MilitaryVerificationErrorContext =
  createContext<MilitaryVerificationErrorContextValue>({
    error: null,
    setError: noop,
    clearError: noop,
  });

export const MilitaryVerificationErrorProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [error, setError] = useState<MilitaryVerificationBannerError | null>(
    null,
  );
  const clearError = useCallback(() => setError(null), []);
  const value = useMemo(
    () => ({ error, setError, clearError }),
    [error, clearError],
  );

  return (
    <MilitaryVerificationErrorContext.Provider value={value}>
      {children}
    </MilitaryVerificationErrorContext.Provider>
  );
};

export const useMilitaryVerificationError = () =>
  useContext(MilitaryVerificationErrorContext);
